import { Alignment, BoxModelConstraint, CellPanel, ColumnComposition, ColumnCompositions, RowPanel } from '@blk/simba';
import { AdvisoryFeeUtil } from '@ddt-web/advisory-fee';
import { CellData, CellUtil, ComponentData, Expression, NumberFormatter, TabData, TableData, TabUtil } from '@ddt-web/data-service';
import * as get from 'lodash/get';
import * as isNil from 'lodash/isNil';
import * as set from 'lodash/set';
import * as toNumber from 'lodash/toNumber';

import { HighchartsSVGComponent } from '../../highchart/highchart-svg-component';

export class ScenarioHelper {
	public static readonly hardBorderColour = '#616161';
	public static readonly lightBorderColour = '#E2E2E2';
	public static readonly thickBorder = 1;
	public static readonly thinBorder = 0.25;
	public static readonly cellBorder: Partial<BoxModelConstraint> = {
		borderLeft: ScenarioHelper.thinBorder,
		borderLeftColor: ScenarioHelper.lightBorderColour,
		borderRight: ScenarioHelper.thinBorder,
		borderRightColor: ScenarioHelper.lightBorderColour
	};
	public static readonly cellRightBorder: Partial<BoxModelConstraint> = {
		borderRight: ScenarioHelper.thinBorder,
		borderRightColor: ScenarioHelper.lightBorderColour
	};
	public static readonly rowBorder: Partial<BoxModelConstraint> = {
		borderTop: ScenarioHelper.thinBorder,
		borderBottom: ScenarioHelper.thinBorder,
		borderLeft: ScenarioHelper.thinBorder,
		borderRight: ScenarioHelper.thinBorder,
		borderTopColor: ScenarioHelper.lightBorderColour,
		borderBottomColor: ScenarioHelper.lightBorderColour,
		borderLeftColor: ScenarioHelper.hardBorderColour,
		borderRightColor: ScenarioHelper.hardBorderColour
	};
	public static readonly textClasses = ['text-bold', 'text-8'];
	public static readonly condensedTextClasses = ['text-condensed-light', 'text-8'];
	public static readonly condensedBoldTextClasses = ['text-condensed-bold', 'text-8'];

	public static getHeaderBorder(isPrimaryHeader: boolean, isSecondaryHeaderToAdd?: boolean): Partial<BoxModelConstraint> {
		const border = Object.assign({}, ScenarioHelper.rowBorder);
		border.borderTopColor = ScenarioHelper.hardBorderColour;

		if (isPrimaryHeader) {
			if (isSecondaryHeaderToAdd) {
				// table has multiple header
				border.borderBottom = 0;
			}
		} else {
			border.borderTop = 0;
		}

		return border;
	}

	public static getCellBorder(isMdsHeader: boolean): Partial<BoxModelConstraint> {
		const border = Object.assign({}, ScenarioHelper.cellBorder);

		if (isMdsHeader) {
			border.borderBottom = ScenarioHelper.thinBorder;
			border.borderBottomColor = ScenarioHelper.lightBorderColour;
		}

		return border;
	}

	/**
	 * This method is needed as the paddingTop and paddingBottom should be increased when portfolio name is larger,
	 * as the border is not going to cover the full length of the cell.
	 *
	 * @param {string} portfolioName
	 */
	public static getVerticalCellPadding(portfolioName: string): number {
		let padding: number = 9;

		if (portfolioName) {
			if (portfolioName.length > 80) {
				padding = 35;
			} else if (portfolioName.length > 50) {
				padding = 25;
			} else if (portfolioName.length > 25) {
				padding = 15;
			}
		}

		return padding;
	}

	/**
	 * Returns the text classes based on the provided params.
	 * - if cell is includeYield column: text-black (no matter what the value is)
	 * - if value is 0 and primaryValue: text-black and text-bold
	 * - if value not primaryValue: text-grey
	 * - if value value > 0 and primaryValue: text-st-table-green and text-bold (colour green)
	 * - if value value < 0 and primaryValue: text-st-table-red and text-bold (colour green)
	 *
	 * @static
	 * @param {CellData} cell CellData with value
	 * @param {number} cellIndex index of the provided cell value
	 * @param {boolean} includeYield true if the table should contain yield (not necessary the current cell), false otherwise
	 * @param {boolean} isPrimaryValue true if the provided cell value is primary, false otherwise
	 * @returns {Array<string>} text classes array to be applied to the provided cell text value
	 */
	public static getCellTextClasses(cell: CellData, cellIndex: number, includeYield: boolean, isPrimaryValue: boolean): Array<string> {
		const value = parseFloat(cell.rawValue as string);

		if (includeYield && cellIndex === 0) {
			if (isPrimaryValue) {
				return ['text-black', 'text-bold'];
			}
			return ['text-grey'];
		} else if (value === 0 && isPrimaryValue) {
			return ['text-black', 'text-bold'];
		} else if (!isPrimaryValue) {
			return ['text-grey'];
		} else if (isPrimaryValue) {
			return value > 0 ? ['text-st-table-green', 'text-bold'] : ['text-st-table-red', 'text-bold'];
		}
	}

	/**
	 * Returns CellData containing the value amount calculated based on the provided percentage CellData and aumValue.
	 *
	 * @static
	 * @param {CellData} cell percentage CellData
	 * @param {number} aumValue default aum value
	 * @returns {CellData} value amount CellData
	 */
	public static getValueAmount(cell: CellData, aumValue: number, shouldAddValuePrefix: boolean = true): CellData {
		let percentageRawValue = get(cell, 'rawValue', 0);

		const formatter = new NumberFormatter(NumberFormatter.NONE, 0, NumberFormatter.NOT_APPLICABLE, NumberFormatter.DASH_VALUE, NumberFormatter.NONE, null, false, null, false, 'NaN');
		let valueToFormat = percentageRawValue;
		let prefix = '';

		// set prefix if provided percentage value is not nil
		if (!isNil(percentageRawValue)) {
			if (percentageRawValue > 0 && shouldAddValuePrefix) {
				prefix = '+ $';
			} else if (percentageRawValue < 0 && shouldAddValuePrefix) {
				prefix = '- $';
			} else {
				prefix = '$';
			}

			// calculate the amount based on the provided defaultAum and aumValue
			const isPositiveValue = !isNil(percentageRawValue) && percentageRawValue > 0;
			percentageRawValue = isPositiveValue ? percentageRawValue : percentageRawValue * -1;

			valueToFormat = percentageRawValue * aumValue / 100;
		}

		const dollarRawValue = formatter.format(valueToFormat);
		const dollarFormattedValue = prefix + formatter.format(valueToFormat);

		return new CellData(null, dollarRawValue, dollarFormattedValue);
	}

	/**
	 * Updates the CellData object to include yield value and/or advisory fee.
	 * If the rawValue of the provided CellData is nil, nothing changes.
	 *
	 * @static
	 * @param {CellData} cellData CellData object to be updated
	 * @param {string} yieldValue
	 * @param {boolean} includeYield
	 * @param {number} advisoryFeeAmount
	 */
	public static updatePercentageCellData(cellData: CellData, yieldValue: string, includeYield: boolean, advisoryFeeAmount: number = 0): void {
		const rawValue: any = get(cellData, 'rawValue');

		if (!isNil(rawValue)) {
			let value: number = toNumber(rawValue) || 0;
			if (includeYield) {
				const floatYield: number = toNumber(yieldValue) || 0;
				value = value + floatYield;
			}

			value = AdvisoryFeeUtil.calculateValueWithScenarioAdvisoryFee(value, advisoryFeeAmount, includeYield);
			const prefix = value > 0 ? '+' : '';

			// update rawValue and formattedValue to include yield
			set(cellData, 'rawValue', value);
			set(cellData, 'formattedValue', prefix + value.toFixed(2) + '%');
		}
	}

	public static getHorizontalAlignmentBasedOnIndex(index: number, isMdsHeader: boolean = false, textValuesIndexesArray: number[] = [0]): Alignment.Horizontal {
		let horizontalAlignment = Alignment.Horizontal.Center;

		if (!isMdsHeader) {
			horizontalAlignment = textValuesIndexesArray.includes(index) ? Alignment.Horizontal.Left : Alignment.Horizontal.Right;
		}

		return horizontalAlignment;
	}

	public static createGenericTableRow(name: string, columnConfigurations: ColumnComposition[], border: Partial<BoxModelConstraint>, vAlign = Alignment.Vertical.Bottom): RowPanel {
		return new RowPanel(
			name,
			columnConfigurations.map((config: ColumnComposition) => config.renderer.render()),
			border,
			Alignment.Horizontal.Left,
			vAlign
		);
	}

	public static createScenarioBodyRows(
		name: string, columnConfigurations: ColumnCompositions, border: Partial<BoxModelConstraint>, addBottomMargin: boolean = false, includeYield = false): Array<RowPanel> {

		const rows: RowPanel[] = [];
		columnConfigurations.body.forEach((configs: Array<ColumnComposition>, index: number) => {
			if (index === columnConfigurations.body.length - 1) {
				border.borderBottomColor = ScenarioHelper.hardBorderColour;
				if (addBottomMargin) {
					border = { ...border, ...{ marginBottom: 15 } };
				}
			}

			if (includeYield) {
				index === 0 ? (border.borderBottom = ScenarioHelper.thickBorder) : (border.borderBottom = ScenarioHelper.thinBorder);
			}

			const rowRenderer = configs.map((config: ColumnComposition) => config.renderer.render());
			rows.push(new RowPanel(name, rowRenderer, border, Alignment.Horizontal.Left, Alignment.Vertical.Middle));
		});

		return rows;
	}

	public static createScenarioBodyChartRows(
		name: string,
		columnConfigurations: ColumnCompositions,
		border: Partial<BoxModelConstraint>,
		tabData: TabData,
		customAttributes,
		addBottomMargin: boolean = false
	): Array<RowPanel> {

		const tabs = TabUtil.extractMultipleTabsFromTabData(tabData);
		const rows: RowPanel[] = [];
		columnConfigurations.body.forEach((configs: Array<ColumnComposition>, index: number) => {
			if (index === columnConfigurations.body.length - 1) {
				border.borderBottomColor = ScenarioHelper.hardBorderColour;
				if (addBottomMargin) {
					border = { ...border, ...{ marginBottom: 15 } };
				}
			}
			rows.push(
				new RowPanel(
					name,
					[
						configs[0].renderer.render(),
						new CellPanel(
							[
								new HighchartsSVGComponent(
									'scenario-chart',
									{},
									tabs[index],
									'bar',
									customAttributes
								)
							],
							configs[1].boxModelConstraint,
							Alignment.Horizontal.Left
						)
					],
					border
				)
			);
		});

		return rows;
	}

	public static getFilteredTabData(
		componentData: ComponentData,
		defaultScenarioKeys: string[],
		includeYield: boolean = false,
		enableScenarioAdvisoryFee: boolean = false,
		advisoryFeeAmount: number = 0,
		tabId: string = 'scenarios.marketMoves',
		scenarioCategory?: string
	): TabData {
		const filteredIndexArray: number[] = [];
		const marketMovesTabData: TabData = componentData.getTabById(tabId);
		const yieldTabData: TabData = componentData.getTabById('scenarios.yield');
		const headersY: CellData[] = get(marketMovesTabData, 'tableData[0].headersY', []);
		let userScenarios = CellUtil.findByExpression(componentData, [new Expression('users.selected.portfolios', 'tabs', 'tableData[0].cells[0][0].rawValue')]);
		// const defaultScenarioKeys = contentService.get(ScenarioTesterDCR.DEFAULT_SCENARIOS_KEY, ScenarioTesterDCR.DEFAULT_SCENARIOS_VALUE).split(',');
		let filteredHeadersY: CellData[];
		if (!isNil(scenarioCategory)) {
			userScenarios = get(userScenarios, scenarioCategory, []);
		}

		if (userScenarios && userScenarios.length) {
			filteredHeadersY = headersY.filter((scenario, index) => {
				const isUserSelected = userScenarios.includes(scenario.rawValue);
				if (isUserSelected) {
					filteredIndexArray.push(index);
				}
				return isUserSelected;
			});
		} else {
			filteredHeadersY = headersY.filter((scenario, index) => {
				const isDefault = defaultScenarioKeys.includes(<string>scenario.rawValue);
				if (isDefault) {
					filteredIndexArray.push(index);
				}
				return isDefault;
			});
		}

		let filteredCells: CellData[][] = marketMovesTabData.tableData[0].cells.filter((row, index) => {
			return filteredIndexArray.includes(index);
		});

		if (includeYield && yieldTabData && !yieldTabData.error) {
			filteredCells = filteredCells.map(cellArray => {
				return cellArray.map((cell, portfolioIndex) => {
					ScenarioHelper.updatePercentageCellData(
						cell,
						yieldTabData.tableData[0].cells[0][portfolioIndex].rawValue as string,
						true,
						enableScenarioAdvisoryFee ? advisoryFeeAmount : 0
					);
					return cell;
				});
			});
		}

		if (!includeYield && enableScenarioAdvisoryFee) {
			filteredCells = filteredCells.map(cellArray => {
				return cellArray.map((cell) => {
					ScenarioHelper.updatePercentageCellData(
						cell,
						null,
						false,
						advisoryFeeAmount
					);
					return cell;
				});
			});
		}

		const filteredTableData: TableData = new TableData(
			get(marketMovesTabData, 'tableData[0].headersX', []),
			filteredHeadersY,
			filteredCells,
			get(marketMovesTabData, 'tableData[0].portfolioData', []),
			get(marketMovesTabData, 'tableData[0].firstColumnTitle', [])
		);

		return new TabData(marketMovesTabData.config, new Array(filteredTableData), marketMovesTabData.displayConfig);
	}

}
